import { useState, useRef, useCallback, useEffect } from 'react';

interface PinInputProps {
  length?: number;
  onComplete: (pin: string) => void;
  onChange?: (pin: string, isComplete: boolean) => void;
  disabled?: boolean;
  error?: string | null;
}

export default function PinInput({
  length = 6,
  onComplete,
  onChange,
  disabled = false,
  error = null,
}: PinInputProps) {
  const [pin, setPin] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus();
  }, []);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (disabled) return;

      // Only allow characters from PIN charset (excludes confusing chars)
      // Allowed: A-H, J-K, M-N, P, Q, R, T-Y, 3-4, 6-9
      // Excluded: 0, O, 1, I, L, 5, S, 2, Z
      const sanitized = value.toUpperCase().replace(/[^ABCDEFGHJKMNPQRTUVWXY346789]/g, '').slice(0, 1);

      if (sanitized) {
        const newPin = [...pin];
        newPin[index] = sanitized;
        setPin(newPin);

        const pinString = newPin.join('');
        const isComplete = pinString.length === length && newPin.every((c) => c !== '');

        onChange?.(pinString, isComplete);

        if (isComplete) {
          onComplete(pinString);
        } else if (index < length - 1) {
          focusInput(index + 1);
        }
      }
    },
    [pin, length, disabled, onComplete, onChange, focusInput]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.key === 'Backspace') {
        if (pin[index] === '' && index > 0) {
          const newPin = [...pin];
          newPin[index - 1] = '';
          setPin(newPin);
          focusInput(index - 1);
          onChange?.(newPin.join(''), false);
        } else {
          const newPin = [...pin];
          newPin[index] = '';
          setPin(newPin);
          onChange?.(newPin.join(''), false);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [pin, length, disabled, focusInput, onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      if (disabled) return;

      const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^ABCDEFGHJKMNPQRTUVWXY346789]/g, '').slice(0, length);

      if (pasted) {
        const newPin = pasted.split('').slice(0, length);
        while (newPin.length < length) {
          newPin.push('');
        }
        setPin(newPin);

        const pinString = newPin.join('');
        const isComplete = pinString.length === length && newPin.every((c) => c !== '');

        onChange?.(pinString, isComplete);

        if (isComplete) {
          onComplete(pinString);
        } else {
          focusInput(Math.min(pasted.length, length - 1));
        }
      }
    },
    [length, disabled, onComplete, onChange, focusInput]
  );

  // Reset when error changes
  useEffect(() => {
    if (error) {
      setPin(Array(length).fill(''));
      focusInput(0);
    }
  }, [error, length, focusInput]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-2 sm:gap-3">
        {pin.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="text"
            maxLength={1}
            value={digit}
            disabled={disabled}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={`pin-input ${digit ? 'filled' : ''} ${error ? 'border-red-500 bg-red-50' : ''} disabled:opacity-50`}
            aria-label={`PIN digit ${index + 1}`}
          />
        ))}
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-1 text-red-600 text-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
    </div>
  );
}
